<template>
  <div class="mall-my-list">
    <header class="mall-my-list__head">
      <h1>취소 · 반품 · 환불 내역</h1>
      <p>요청 중이거나 처리된 취소·반품 주문을 모았습니다.</p>
    </header>

    <div v-if="loading" class="mall-my-list__empty">불러오는 중입니다…</div>

    <div v-else-if="!rows.length" class="mall-my-list__empty">
      <AppIcon icon="mdi-clipboard-check-outline" :size="48" />
      <p>취소·반품 내역이 없습니다</p>
      <router-link :to="MALL_ORDERS_PATH">주문목록 보기</router-link>
    </div>

    <ul v-else class="mall-my-list__cards">
      <li v-for="row in rows" :key="`${row.orderCode}-${row.itemIdx}`">
        <div class="mall-my-list__meta">
          <strong>{{ row.statusLabel }}</strong>
          <time>{{ row.orderedAt }}</time>
        </div>
        <p class="mall-my-list__title">{{ row.productName }}</p>
        <p v-if="row.option" class="mall-my-list__sub">{{ row.option }}</p>
        <p class="mall-my-list__sub">
          주문 {{ row.orderCode }} · 환불 {{ formatPrice(row.refundAmount) }}원
          <template v-if="row.returnCause"> · {{ row.returnCause === 'CHANGE' ? '단순 변심' : '하자·오배송' }}</template>
          <template v-if="row.claimReason"> · {{ row.claimReason }}</template>
        </p>
        <div class="mall-my-list__actions">
          <router-link :to="`/mall/orders/${row.orderCode}`">주문 상세</router-link>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  MALL_ORDER_ITEM_STATUS_LABEL,
  fetchMyOrder,
  fetchMyOrders,
  type MallOrderItemStatus,
} from '@/api/mallOrder'
import { formatPrice } from '@/constants/mallProducts'
import { MALL_ORDERS_PATH } from '@/constants/mall'

interface ClaimRow {
  orderCode: string
  itemIdx: number
  productName: string
  option: string
  amount: number
  refundAmount: number
  returnCause: string
  orderedAt: string
  statusLabel: string
  claimReason: string
}

const CLAIM_STATUSES: MallOrderItemStatus[] = ['CANCEL_REQUEST', 'RETURN_REQUEST', 'CANCELED']

const loading = ref(true)
const rows = ref<ClaimRow[]>([])

onMounted(async () => {
  loading.value = true
  try {
    const orders = await fetchMyOrders()
    const claimOrders = orders.filter((order) =>
      (order.itemStatuses || []).some((status) => CLAIM_STATUSES.includes(status)),
    )
    const details = await Promise.all(
      claimOrders.map((order) => fetchMyOrder(order.code).catch(() => null)),
    )
    const next: ClaimRow[] = []
    for (const detail of details) {
      const order = detail?.order
      if (!order) continue
      for (const item of order.items || []) {
        if (!CLAIM_STATUSES.includes(item.status)) continue
        next.push({
          orderCode: order.code,
          itemIdx: item.idx,
          productName: item.name || '상품',
          option: item.option || '',
          amount: item.amount || 0,
          refundAmount: item.refundAmount || item.amount || 0,
          returnCause: item.returnCause || '',
          orderedAt: String(order.orderedAt || '').slice(0, 10),
          statusLabel: MALL_ORDER_ITEM_STATUS_LABEL[item.status] || item.status,
          claimReason: item.claimReason || '',
        })
      }
    }
    rows.value = next
  } catch {
    rows.value = []
  } finally {
    loading.value = false
  }
})
</script>

<style lang="scss" src="./MallMyListPage.scss"></style>
